import Toastify from "toastify-js";
import "@/assets/css/vendors/toastify.css";
import { createIcons, icons } from "lucide";

// Helper to create HTML string for the toast content
const createToastContent = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    const iconName = type === 'success' ? 'check-circle' :
        type === 'error' ? 'x-circle' :
            type === 'warning' ? 'alert-circle' : 'info';

    const colorClass = type === 'success' ? 'text-success' :
        type === 'error' ? 'text-danger' :
            type === 'warning' ? 'text-warning' : 'text-primary';

    // We return a DOM node or string. Toastify accepts DOM node.
    const container = document.createElement('div');
    container.className = "flex";

    // Icon
    const iconContainer = document.createElement('div');
    iconContainer.innerHTML = `<i data-lucide="${iconName}" class="${colorClass}"></i>`;
    container.appendChild(iconContainer);

    // Text
    const textContainer = document.createElement('div');
    textContainer.className = "ml-4 mr-4";

    const titleEl = document.createElement('div');
    titleEl.className = "font-medium";
    titleEl.innerText = title;

    const msgEl = document.createElement('div');
    msgEl.className = "mt-1 text-slate-500";
    msgEl.innerText = message;

    textContainer.appendChild(titleEl);
    textContainer.appendChild(msgEl);
    container.appendChild(textContainer);

    return container;
};

export const useNotification = () => {
    const show = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
        const content = createToastContent(title, message, type);

        const toast = Toastify({
            node: content,
            duration: 3000,
            newWindow: true,
            close: true,
            gravity: "top",
            position: "right",
            stopOnFocus: true,
            className: "!flex !py-5 !pl-5 !pr-14 !bg-white !border !rounded-lg !shadow-xl !border-slate-200/60 dark:!bg-darkmode-600 dark:!text-slate-300 dark:!border-darkmode-600",
            style: {
                background: "white", // Fallback
                boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)"
            }
            // We need to override the default styles if we use Tailwind classes manually on the node
            // But Toastify appends its own classes. 
            // The template uses `class` prop in `Notification.vue` which Toastify applies to the container.
            // Let's use the same classes as `Notification.vue`.
        }).showToast();

        // Render icons
        createIcons({
            icons,
            attrs: { "stroke-width": 1.5 },
            nameAttr: "data-lucide",
        });
    };

    const success = (title: string, message: string) => show(title, message, 'success');
    const error = (title: string, message: string) => show(title, message, 'error');
    const warning = (title: string, message: string) => show(title, message, 'warning');
    const info = (title: string, message: string) => show(title, message, 'info');

    return {
        show,
        success,
        error,
        warning,
        info
    };
};
